import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";

/**
 * Modal quando uma regra de automação bloqueia uma ação.
 * A mensagem parametrizada na regra é o foco visual principal.
 */
export default function AutomationActionBlockedDialog({
  open = false,
  message = "",
  onClose,
}) {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const ruleMessage =
    String(message || "").trim() ||
    t("automationBlock.fallback", {
      defaultValue: "Esta ação foi bloqueada por uma regra de automação.",
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="automation-block-dialog-title"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        id="automation-block-dialog-title"
        sx={{ pb: 1, fontWeight: 700, fontSize: "1.125rem" }}
      >
        {t("automationBlock.title", { defaultValue: "Ação bloqueada" })}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5, pb: 3 }}>
        <Paper
          variant="outlined"
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            borderLeftWidth: 4,
            borderLeftStyle: "solid",
            borderLeftColor: "warning.main",
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            borderColor: alpha(theme.palette.warning.main, 0.35),
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: "block",
              mb: 1,
              color: "warning.dark",
              fontWeight: 700,
              letterSpacing: 0.6,
              lineHeight: 1.4,
            }}
          >
            {t("automationBlock.messageLabel", { defaultValue: "O que você precisa saber" })}
          </Typography>
          <Typography
            component="p"
            variant="h6"
            sx={{
              m: 0,
              fontWeight: 700,
              fontSize: { xs: "1.15rem", sm: "1.25rem" },
              lineHeight: 1.45,
              color: "text.primary",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {ruleMessage}
          </Typography>
        </Paper>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mt: 2,
            px: 0.25,
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: "text.secondary", mt: 0.15, flexShrink: 0 }}
            aria-hidden
          />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            {t("automationBlock.subtitle", {
              defaultValue: "Uma regra de automação impediu esta alteração.",
            })}{" "}
            {t("automationBlock.hint", {
              defaultValue:
                "Ajuste os dados necessários e tente novamente.",
            })}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="contained" color="primary" onClick={onClose} autoFocus>
          {t("automationBlock.confirm", { defaultValue: "Entendi" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
